import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CalendarDays, MapPin, ArrowRight, Loader2, CalendarX } from "lucide-react";
import api, { resolveImg } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { useSeo } from "@/hooks/useSeo";
import PageHeader from "@/components/PageHeader";
import Pagination from "@/components/Pagination";
import ImageFallback from "@/components/ImageFallback";
import { usePagination } from "@/hooks/usePagination";

const Kegiatan = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const { page, totalPages, pageItems, setPage, total, pageSize } = usePagination(items);
  useSeo("Kegiatan", "Agenda dan kegiatan sekolah Madrasah Ibtidaiyah Futuhiyyah Doro, Pekalongan.");

  useEffect(() => {
    api.get("/activities").then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div>
      <PageHeader title="Kegiatan Sekolah" subtitle="Beragam kegiatan dan agenda yang mengasah potensi serta karakter siswa." crumbs={[{ label: "Kegiatan" }]} />
      <div className="mx-auto max-w-6xl px-6 py-14">
        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-emerald-600" /></div>
        ) : items.length === 0 ? (
          <div className="py-20 text-center text-slate-400" data-testid="activities-empty-state">
            <CalendarX className="mx-auto h-12 w-12 opacity-40" />
            <p className="mt-3">Belum ada kegiatan yang tersedia.</p>
          </div>
        ) : (
          <>
          <p className="mb-6 text-center text-sm text-slate-400" data-testid="activities-count-info">
            Menampilkan {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} dari {total} kegiatan
          </p>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="activities-grid">
            {pageItems.map((a, i) => (
              <motion.div key={`${page}-${a.id}`} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: i * 0.04 }}>
                <Link to={`/kegiatan/${a.slug}`} data-testid={`activity-card-${a.id}`} className="group flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-md">
                  <div className="aspect-[16/10] overflow-hidden">
                    {a.image_url ? (
                      <img src={resolveImg(a.image_url)} alt={a.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    ) : (
                      <ImageFallback />
                    )}
                  </div>
                  <div className="flex flex-1 flex-col p-5">
                    <div className="flex flex-wrap items-center gap-3 text-xs text-slate-400">
                      <span className="flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" />{formatDate(a.date)}</span>
                      {a.location && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{a.location}</span>}
                    </div>
                    <h3 className="mt-2 font-heading text-base font-semibold text-slate-800 line-clamp-2 group-hover:text-emerald-700">{a.title}</h3>
                    <p className="mt-2 flex-1 text-sm text-slate-500 line-clamp-3">{a.excerpt}</p>
                    <span className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-emerald-700">Selengkapnya <ArrowRight className="h-3.5 w-3.5" /></span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
          <Pagination page={page} totalPages={totalPages} onChange={setPage} prefix="activities-pagination" />
          </>
        )}
      </div>
    </div>
  );
};

export default Kegiatan;
